/**
 * Bath & Sanitary — Legacy image URL resolver (generalized)
 *
 * Lovable hosting does not process _redirects / _headers files, so we cannot
 * issue HTTP 301s for renamed static assets. Policy:
 *
 *   1. CANONICAL FILENAMES NEVER CHANGE. New versions of an asset must be
 *      uploaded by overwriting the existing file at the same path.
 *      See public/images/bath/README.md.
 *
 *   2. This map is an in-app safety net: any legacy/alternate path used in
 *      our components, blog posts, or CMS content is transparently rewritten
 *      to the canonical path before the browser requests it. External
 *      hotlinks (Google Images, partner pages) hit the static file directly
 *      and are unaffected.
 *
 * Add a new entry whenever a bath asset is renamed, moved, or deprecated.
 */

/** Canonical (current) bath asset paths. Do not rename these files. */
export const BATH_CANONICAL = {
  // Aria Sculpt
  ariaSculptHero: "/images/bath/aria-sculpt-hero.jpg",
  ariaSculptDetail: "/images/bath/aria-sculpt-detail.jpg",
  ariaSculptTop: "/images/bath/aria-sculpt-top.jpg",
  ariaSculptNoir: "/images/bath/aria-sculpt-noir.jpg",
  ariaSculptNoirTop: "/images/bath/aria-sculpt-noir-top.jpg",

  // Aria Monolith (variant bathtub)
  ariaMonolith: "/images/bath/aria-monolith.jpg",
  ariaMonolithDetail: "/images/bath/aria-monolith-detail.jpg",
  ariaMonolithSide: "/images/bath/aria-monolith-side.jpg",
  ariaMonolithSuite: "/images/bath/aria-monolith-suite.jpg",
  ariaMonolithVilla: "/images/bath/aria-monolith-villa.jpg",

  // Aria Diamond (diamond-textured freestanding bathtub)
  ariaDiamondHero: "/images/bath/aria-diamond-hero.jpg",
  ariaDiamondDetail: "/images/bath/aria-diamond-detail.jpg",
  ariaDiamondTop: "/images/bath/aria-diamond-top.jpg",
  ariaDiamondNoir: "/images/bath/aria-diamond-noir.jpg",
  ariaDiamondSuite: "/images/bath/aria-diamond-suite.jpg",

  // Vena Counter Basin
  venaBasin: "/images/bath/vena-basin.jpg",
  venaBasinDetail: "/images/bath/vena-basin-detail.jpg",
  venaBasinTop: "/images/bath/vena-basin-top.jpg",
  venaBasinSuite: "/images/bath/vena-basin-suite.jpg",
  venaBasinResidence: "/images/bath/vena-basin-residence.jpg",

  // Vena Vanity Console
  venaVanity: "/images/bath/vena-vanity.jpg",
  venaVanityDetail: "/images/bath/vena-vanity-detail.jpg",
  venaVanitySide: "/images/bath/vena-vanity-side.jpg",
  venaVanitySuite: "/images/bath/vena-vanity-suite.jpg",
  venaVanityResidence: "/images/bath/vena-vanity-residence.jpg",

  // Lifestyle scenes (shared across products)
  lifestyleSuite: "/images/bath/lifestyle-suite.jpg",
  lifestyleResidence: "/images/bath/lifestyle-residence.jpg",
} as const;

/**
 * Legacy → canonical path map across all bath products. Keys are any
 * historical/alternate paths we have ever shipped or might encounter.
 * Keys must be lowercase. Resolution is case-insensitive.
 */
const LEGACY_TO_CANONICAL: Record<string, string> = {
  // ───────── Aria Sculpt ─────────
  "/images/bath/aria-sculpt-v1.jpg": BATH_CANONICAL.ariaSculptHero,
  "/images/bath/aria-sculpt-main.jpg": BATH_CANONICAL.ariaSculptHero,
  "/images/bath/aria-sculpt.jpg": BATH_CANONICAL.ariaSculptHero,
  "/images/bath/aria-tub-hero.jpg": BATH_CANONICAL.ariaSculptHero,
  "/images/bath/aria-tub-hero.png": BATH_CANONICAL.ariaSculptHero,

  "/images/bath/aria-sculpt-closeup.jpg": BATH_CANONICAL.ariaSculptDetail,
  "/images/bath/aria-sculpt-fluted.jpg": BATH_CANONICAL.ariaSculptDetail,
  "/images/bath/aria-tub-detail.jpg": BATH_CANONICAL.ariaSculptDetail,

  "/images/bath/aria-monolith-tub.jpg": BATH_CANONICAL.ariaMonolith,
  "/images/bath/aria-oval.jpg": BATH_CANONICAL.ariaMonolith,

  // ───────── Vena ─────────
  "/images/bath/vena-basin-v1.jpg": BATH_CANONICAL.venaBasin,
  "/images/bath/vena-washbasin.jpg": BATH_CANONICAL.venaBasin,
  "/images/bath/vena-sink.jpg": BATH_CANONICAL.venaBasin,
  "/images/bath/vena-basin-hero.jpg": BATH_CANONICAL.venaBasin,

  "/images/bath/vena-vanity-v1.jpg": BATH_CANONICAL.venaVanity,
  "/images/bath/vena-vanity-unit.jpg": BATH_CANONICAL.venaVanity,
  "/images/bath/vena-cabinet.jpg": BATH_CANONICAL.venaVanity,

  // ───────── Lifestyle (shared) ─────────
  "/images/bath/aria-sculpt-hotel.jpg": BATH_CANONICAL.lifestyleSuite,
  "/images/bath/aria-sculpt-suite.jpg": BATH_CANONICAL.lifestyleSuite,
  "/images/bath/lifestyle-hotel.jpg": BATH_CANONICAL.lifestyleSuite,
  "/images/bath/bath-suite.jpg": BATH_CANONICAL.lifestyleSuite,

  "/images/bath/aria-sculpt-villa.jpg": BATH_CANONICAL.lifestyleResidence,
  "/images/bath/aria-sculpt-residence.jpg": BATH_CANONICAL.lifestyleResidence,
  "/images/bath/lifestyle-villa.jpg": BATH_CANONICAL.lifestyleResidence,
  "/images/bath/bath-residence.jpg": BATH_CANONICAL.lifestyleResidence,

  // ───────── Common asset-folder leaks (early dev paths) ─────────
  "/src/assets/aria-sculpt-hero.jpg": BATH_CANONICAL.ariaSculptHero,
  "/src/assets/aria-sculpt.jpg": BATH_CANONICAL.ariaSculptHero,
  "/src/assets/vena-basin.jpg": BATH_CANONICAL.venaBasin,
  "/src/assets/vena-vanity.jpg": BATH_CANONICAL.venaVanity,
};

/** All known canonical paths, used for the onError safety check. */
const CANONICAL_PATHS = new Set<string>(Object.values(BATH_CANONICAL));

/**
 * Per-product fallback hierarchy. If a canonical asset itself 404s, we fall
 * back to the most representative remaining image for that product family.
 */
const PRODUCT_FALLBACK_CHAIN: Array<{ match: RegExp; fallback: string }> = [
  { match: /aria-diamond/i, fallback: BATH_CANONICAL.ariaDiamondHero },
  { match: /aria-monolith/i, fallback: BATH_CANONICAL.ariaSculptHero },
  { match: /aria-sculpt-detail/i, fallback: BATH_CANONICAL.ariaSculptHero },
  { match: /aria-sculpt/i, fallback: BATH_CANONICAL.ariaSculptHero },
  { match: /vena-vanity/i, fallback: BATH_CANONICAL.venaBasin },
  { match: /vena-basin/i, fallback: BATH_CANONICAL.venaVanity },
  { match: /lifestyle-residence/i, fallback: BATH_CANONICAL.lifestyleSuite },
  { match: /lifestyle-suite/i, fallback: BATH_CANONICAL.lifestyleResidence },
];

/** Generic last-resort fallback when nothing else matches. */
const DEFAULT_FALLBACK = BATH_CANONICAL.ariaSculptHero;

/**
 * Rewrite a possibly-legacy bath image src to its canonical path.
 * Returns the input unchanged if it is already canonical or unknown.
 */
export function resolveBathImage(src: string): string {
  if (!src) return src;
  const key = src.split("?")[0].split("#")[0].toLowerCase();
  return LEGACY_TO_CANONICAL[key] ?? src;
}

/**
 * Given a broken bath image src (after onError), return the best-guess
 * canonical fallback, or null if we have no opinion.
 */
export function fallbackForBrokenBathImage(src: string): string | null {
  if (!src) return null;
  const key = src.split("?")[0].split("#")[0].toLowerCase();

  // 1. Direct legacy mapping wins.
  if (LEGACY_TO_CANONICAL[key]) return LEGACY_TO_CANONICAL[key];

  // 2. If a canonical asset itself failed, use the per-product fallback chain.
  const isCanonical = [...CANONICAL_PATHS].some((p) => key.endsWith(p.toLowerCase()));
  if (isCanonical) {
    for (const { match, fallback } of PRODUCT_FALLBACK_CHAIN) {
      if (match.test(key)) return fallback;
    }
    return DEFAULT_FALLBACK;
  }

  // 3. Anything else under /images/bath/ — give a generic bath fallback.
  if (key.includes("/images/bath/")) return DEFAULT_FALLBACK;

  return null;
}
